import { Dispatch } from "redux";
import {
  Product,
  ProductPageData,
  SellerPageData,
} from "../types/productTypes";
import axios from "axios";
import {
  getFirestore,
  collection,
  query,
  orderBy,
  getDocs,
  DocumentData,
  limit,
  startAfter,
  startAt,
  doc,
  getDoc,
  where,
  CollectionReference,
  QuerySnapshot,
  updateDoc,
} from "firebase/firestore";
import { app } from "../../firebase";
import { db } from "../../firebase";
import { Order } from "../types/orderTypes";
import {
  currentTimestamp,
  differenceInDaysBetweenTwoTimeStamps,
  getCookie,
  getSellerName,
  isFirstOfMonth,
  removeDuplicates,
  setCookie,
  urlWithForwardSlash,
} from "../../utils";
import { User } from "../types/userTypes";
import { RESET_PRODUCT_VIEWS } from "../../constants";

export const FETCH_PRODUCTS_REQUEST = "FETCH_PRODUCTS_REQUEST";
export const FETCH_PRODUCTS_SUCCESS = "FETCH_PRODUCTS_SUCCESS";
export const FETCH_PRODUCTS_FAILURE = "FETCH_PRODUCTS_FAILURE";
export const FETCH_PRODUCTS_RESET = "FETCH_PRODUCTS_RESET";

export const PAGE_SIZE = 18;

export const HARDCODED_CATEGORIES = {
  "latest-toys": "Latest Toys",
  "popular-toys": "Popular Toys",
  "toys-under-10": "Toys Under $10",
  "toys-under-25": "Toys Under $25",
  "gender-neutral": "Gender Neutral",
};

export enum SORT_BY {
  LATEST = "latest",
  POPULAR = "popular",
  LOWEST_PRICE = "lowPrice",
  HIGHEST_PRICE = "highPrice",
  ACTIVE = "active",
  INACTIVE = "inactive",
}

export const fetchProductsRequest = (isNewFetch: boolean = false) => ({
  type: FETCH_PRODUCTS_REQUEST,
  payload: { isNewFetch },
});

export const fetchProductsSuccess = (productPageData: {
  products: Product[];
  totalRecords?: number;
  moreProducts?: Product[];
  similarProducts?: Product[];
  sellerInfo?: User;
}) => ({
  type: FETCH_PRODUCTS_SUCCESS,
  payload: productPageData,
});

export const fetchProductsFailure = (error: string) => ({
  type: FETCH_PRODUCTS_FAILURE,
  payload: error,
});

export const resetProductState = () => ({
  type: FETCH_PRODUCTS_RESET,
  //payload: users,
});

// Define other item-related actions here...
export const createItem = () => async (dispatch: Dispatch) => {
  const newItem = `Item ${Date.now()}`;
  //dispatch({ type: CREATE_ITEM, payload: newItem });
};

export const fetchProducts =
  (
    recordLimit: number,
    prevLastDocument = "",
    categorySelected = "latest-toys",
    productSubCategorySelected = "",
    sortBy: SORT_BY
  ) =>
  async (dispatch: Dispatch) => {
    // isNewFetch is true when prevLastDocument is empty (not pagination)
    const isNewFetch = !prevLastDocument;
    dispatch(fetchProductsRequest(isNewFetch));
    try {
      const productPageData: { products: Product[]; totalRecords?: number } = {
        products: [],
        totalRecords: 0,
      };
      const products: Product[] = [];

      let q = query(collection(db, "products"));
      if (categorySelected !== "latest-toys") {
        if (categorySelected === "popular-toys") {
          if (sortBy === SORT_BY.LATEST) {
            q = query(q, orderBy("createdTime", "desc"));
          }
          if (sortBy === SORT_BY.POPULAR) {
            q = query(q, orderBy("views", "desc"));
          }
          if (sortBy === SORT_BY.LOWEST_PRICE) {
            q = query(q, orderBy("price", "asc"));
          }
          if (sortBy === SORT_BY.HIGHEST_PRICE) {
            q = query(q, orderBy("price", "desc"));
          }
        } else if (categorySelected === "toys-under-10") {
          if (sortBy === SORT_BY.LATEST) {
            q = query(
              q,
              orderBy("price", "desc"),
              where("price", "<=", 10),
              orderBy("createdTime", "desc")
            );
          }
          if (sortBy === SORT_BY.POPULAR) {
            q = query(
              q,
              orderBy("price", "desc"),
              where("price", "<=", 10),
              orderBy("views", "desc")
            );
          }
          if (sortBy === SORT_BY.LOWEST_PRICE) {
            q = query(q, orderBy("price", "asc"), where("price", "<=", 10));
          }
          if (sortBy === SORT_BY.HIGHEST_PRICE) {
            q = query(q, orderBy("price", "desc"), where("price", "<=", 10));
          }
        } else if (categorySelected === "toys-under-25") {
          if (sortBy === SORT_BY.LATEST) {
            q = query(
              q,
              orderBy("price", "desc"),
              where("price", "<=", 25),
              orderBy("createdTime", "desc")
            );
          }
          if (sortBy === SORT_BY.POPULAR) {
            q = query(
              q,
              orderBy("views", "desc"),
              where("price", "<=", 25),
              orderBy("price", "desc")
            );
          }
          if (sortBy === SORT_BY.LOWEST_PRICE) {
            q = query(q, orderBy("price", "asc"), where("price", "<=", 25));
          }
          if (sortBy === SORT_BY.HIGHEST_PRICE) {
            q = query(q, orderBy("price", "desc"), where("price", "<=", 25));
          }
        } else {
          if (productSubCategorySelected) {
            q = query(
              q,
              where("productSubCategorySlug", "==", productSubCategorySelected)
            );
          } else {
            q = query(q, where("productCategorySlug", "==", categorySelected));
          }
          if (sortBy === SORT_BY.LATEST) {
            q = query(q, orderBy("createdTime", "desc"));
          }
          if (sortBy === SORT_BY.POPULAR) {
            q = query(q, orderBy("views", "desc"));
          }
          if (sortBy === SORT_BY.LOWEST_PRICE) {
            q = query(q, orderBy("price", "asc"));
          }
          if (sortBy === SORT_BY.HIGHEST_PRICE) {
            q = query(q, orderBy("price", "desc"));
          }
        }
      } else {
        if (sortBy === SORT_BY.LATEST) {
          q = query(q, orderBy("createdTime", "desc"));
        }
        if (sortBy === SORT_BY.POPULAR) {
          q = query(q, orderBy("views", "desc"));
        }
        if (sortBy === SORT_BY.LOWEST_PRICE) {
          q = query(q, orderBy("price", "asc"));
        }
        if (sortBy === SORT_BY.HIGHEST_PRICE) {
          q = query(q, orderBy("price", "desc"));
        }
      }

      // Query without limit & prevLastDocument
      const totalCountQuery = query(q);
      const totalCountSnapshot = await getDocs(totalCountQuery);
      const total = totalCountSnapshot.size;

      let docSnap;
      if (prevLastDocument) {
        const firestore = getFirestore();
        const docRef = doc(firestore, "products", prevLastDocument);
        docSnap = await getDoc(docRef);
        q = query(q, startAfter(docSnap));
      }

      q = query(q, limit(recordLimit));

      const querySnapshot = await getDocs(q);

      querySnapshot.forEach(async (doc) => {
        const productData = doc.data() as Product;
        productData.documentId = doc.id;
        if (
          categorySelected === "gender-neutral" ||
          categorySelected === "latest-toys" ||
          categorySelected === "popular-toys" ||
          categorySelected === "toys-under-10" ||
          categorySelected === "toys-under-25"
        ) {
          productData.productCategoryName =
            HARDCODED_CATEGORIES[categorySelected];
        }
        products.push(productData);
      });

      if (isFirstOfMonth() && !getCookie(RESET_PRODUCT_VIEWS)) {
        setCookie(RESET_PRODUCT_VIEWS, "true", 1);
        resetProductsViews();
      }

      productPageData.products = products;
      productPageData.totalRecords = total;
      dispatch(fetchProductsSuccess(productPageData));
    } catch (error) {
      console.error(error);
      dispatch(fetchProductsFailure("Error fetching items."));
    }
  };

export const fetchProductById =
  (productUrl: string) => async (dispatch: Dispatch) => {
    // Always clear products for a new product fetch
    dispatch(fetchProductsRequest(true));

    try {
      const returnData: ProductPageData = {
        products: [],
        moreProducts: [],
        similarProducts: [],
        sellerInfo: {
          createdTime: 0,
          email: "",
          isVerified: false,
          stripeid: "",
          name: "",
          loginAttempts: 0,
        },
      };

      const products = await getProductById(productUrl);
      let moreProducts: Product[] = [];
      let similarProducts: Product[] = await getProductByLikeTitle(
        products[0].productName,
        productUrl
      );

      if (products.length > 0) {
        const { productBySeller } = await fetchProductsByOwnerId(
          products[0].ownerId,
          "",
          undefined,
          6,
          productUrl
        );
        moreProducts = productBySeller;
        await updateProduct(products[0], { views: products[0].views + 1 });

        // Fetch seller details by ownerId rather than productOwner (email) -
        // the email on the product can go stale relative to the user's
        // current email, but ownerId (the seller's createdTime) doesn't.
        returnData.sellerInfo = await getSellerDetailsByOwnerId(
          products[0].ownerId
        );
      }
      returnData.products = products;
      returnData.moreProducts = moreProducts;
      returnData.similarProducts = similarProducts;
      dispatch(fetchProductsSuccess(returnData));
    } catch (error) {
      console.error(error);
      dispatch(fetchProductsFailure("Error fetching items."));
    }
  };

export const sellerProductsForProfile =
  (
    sellerTimestamp: number,
    prevLastDocument: string,
    sortBy = SORT_BY.LATEST,
    recordLimit = PAGE_SIZE
  ) =>
  async (dispatch: Dispatch) => {
    dispatch(fetchProductsRequest());

    try {
      const returnData: SellerPageData = {
        products: [],
        totalRecords: 0,
        sellerInfo: {
          createdTime: sellerTimestamp,
          email: "",
          isVerified: false,
          stripeid: "",
          name: "",
          loginAttempts: 0,
        },
      };
      // Fetch seller details
      let q = query(collection(db, "users"));
      q = query(q, where("createdTime", "==", sellerTimestamp));

      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => {
        returnData.sellerInfo.email = doc.data().email;
        returnData.sellerInfo.isVerified = doc.data().isVerified;
        returnData.sellerInfo.createdTime = doc.data().createdTime;
        returnData.sellerInfo.name = getSellerName(doc.data().email);
      });

      // Fetch products of the seller (ownerId is the seller's profileId/createdTime,
      // not the productOwner email — immune to the seller's email ever changing).
      const sellerProducts = await fetchProductsByOwnerId(
        sellerTimestamp,
        prevLastDocument,
        sortBy,
        recordLimit
      );

      returnData.products = sellerProducts.productBySeller;
      returnData.totalRecords = sellerProducts.total;
      dispatch(fetchProductsSuccess(returnData));
    } catch (error) {
      console.error(error);
      dispatch(fetchProductsFailure("Error fetching items."));
    }
  };

const fetchProductsByOwnerId = async (
  ownerId: number,
  prevLastDocument: string,
  sortBy?: SORT_BY,
  recordLimit?: number,
  excludeUrl?: string
) => {
  try {
    // A plain equality filter needs no composite Firestore index. Combining
    // it with a productQuantity filter and/or an orderBy on a different
    // field (createdTime, views, ...) does require one, and that index
    // isn't provisioned in this project - every such query fails with
    // FAILED_PRECONDITION, silently leaving stale data in the redux store.
    // A seller's own catalog is small, so fetch it in one unfiltered/
    // unordered request and filter/sort/paginate in memory instead.
    const q = query(collection(db, "products"), where("ownerId", "==", ownerId));
    const querySnapshot = await getDocs(q);

    let ownerProducts: Product[] = [];
    querySnapshot.forEach((doc) => {
      const docData = doc.data() as Product;
      docData.documentId = doc.id;
      ownerProducts.push(docData);
    });

    if (excludeUrl) {
      ownerProducts = ownerProducts.filter(
        (product) => product.productUrl !== `/${excludeUrl}`
      );
    }

    if (sortBy === SORT_BY.ACTIVE) {
      ownerProducts = ownerProducts.filter(
        (product) => Number(product.productQuantity) > 0
      );
    } else if (sortBy === SORT_BY.INACTIVE) {
      ownerProducts = ownerProducts.filter(
        (product) => Number(product.productQuantity) === 0
      );
    }

    if (sortBy === SORT_BY.POPULAR) {
      ownerProducts.sort((a, b) => Number(b.views) - Number(a.views));
    } else if (sortBy === SORT_BY.LOWEST_PRICE) {
      ownerProducts.sort(
        (a, b) => Number((a as any).price) - Number((b as any).price)
      );
    } else if (sortBy === SORT_BY.HIGHEST_PRICE) {
      ownerProducts.sort(
        (a, b) => Number((b as any).price) - Number((a as any).price)
      );
    } else {
      ownerProducts.sort((a, b) => Number(b.createdTime) - Number(a.createdTime));
    }

    const total = ownerProducts.length;

    let startIndex = 0;
    if (prevLastDocument) {
      const cursorIndex = ownerProducts.findIndex(
        (product) => product.documentId === prevLastDocument
      );
      startIndex = cursorIndex >= 0 ? cursorIndex + 1 : 0;
    }

    const productBySeller = recordLimit
      ? ownerProducts.slice(startIndex, startIndex + recordLimit)
      : ownerProducts.slice(startIndex);

    return { productBySeller, total };
  } catch (error) {
    console.error(error);
    throw new Error("Error fetching orders.");
  }
};

const getProductById = async (productUrl: string) => {
  try {
    const url = urlWithForwardSlash(productUrl);
    const products: Product[] = [];

    /* Query the "products" collection for a document with the specified productUrl. */
    let q = query(collection(db, "products"), where("productUrl", "==", url));

    let docData: Product | undefined;
    const querySnapshot = await getDocs(q);
    querySnapshot.forEach((doc) => {
      docData = doc.data() as Product;
      docData.documentId = doc.id;
      products.push(docData);
    });
    return products;
  } catch (error) {
    console.error(error);
    throw new Error("Error fetching products.");
  }
};

const getProductByLikeTitle = async (
  searchText: string,
  productUrl: string
) => {
  try {
    const products: Product[] = [];

    const q = query(collection(db, "products"));

    let docData: Product | undefined;
    const querySnapshot = await getDocs(q);
    querySnapshot.forEach((doc) => {
      docData = doc.data() as Product;
      if (docData.productUrl !== `/${productUrl}`) {
        const score = calculateSimilarityScore(docData.productName, searchText);
        if (score > 0) {
          products.push({ ...docData, score });
        }
      }
    });
    // Sort results by score in descending order
    products.sort((a, b) => b.score - a.score);
    return products.slice(0, 12);
  } catch (error) {
    console.error(error);
    throw new Error("Error fetching products.");
  }
};

const hasAtLeastFourConsecutiveLetters = (
  str1: string,
  str2: string
): boolean => {
  for (let i = 0; i <= str1.length - 4; i++) {
    const subStr = str1.substring(i, i + 4);
    if (str2.includes(subStr)) {
      return true;
    }
  }
  return false;
};

const calculateSimilarityScore = (
  title: string,
  searchText: string
): number => {
  const titleWords = title.toLowerCase().split(" ");
  const searchWords = searchText.toLowerCase().split(" ");
  let score = 0;

  searchWords.forEach((searchWord) => {
    titleWords.forEach((titleWord) => {
      if (hasAtLeastFourConsecutiveLetters(titleWord, searchWord)) {
        score += 1;
      }
    });
  });

  return score;
};

const updateProduct = async (productData: Product, dataToUpdate: any) => {
  try {
    const docRef = doc(db, "products", productData.documentId);
    await updateDoc(docRef, dataToUpdate);
  } catch (error) {
    console.error("Error updating document: ", error);
  }
};

// const isLastViewsUpdatedCrossedLimit = async () => {
//   try {
//     let q = query(collection(db, "cron_data"));

//     const querySnapshot = await getDocs(q);
//     querySnapshot.forEach(async (doc) => {
//       const docData = doc.data();
//       docData.documentId = doc.id;
//       if (docData.lastUpdatedViews) {
//         const currentTime = currentTimestamp();
//         const days = differenceInDaysBetweenTwoTimeStamps(
//           currentTime,
//           docData.lastUpdatedViews
//         );

//         if (days > 30) {
//           // Update the lastUpdatedViews field
//           await updateCronData(docData, {
//             lastUpdatedViews: currentTime,
//           });
//           return true;
//         }
//         return false;
//       }
//     });
//     return false;
//   } catch (error) {
//     console.error(error);
//     return false;
//   }
// };

// const updateCronData = async (cronData: any, dataToUpdate: any) => {
//   try {
//     const docRef = doc(db, "cron_data", cronData.documentId);
//     await updateDoc(docRef, dataToUpdate);
//   } catch (error) {
//     console.error("Error updating document: ", error);
//   }
// };

const getSellerDetailsByOwnerId = async (ownerId: number) => {
  try {
    const seller: User = {
      createdTime: 0,
      email: "",
      isVerified: false,
      name: "",
      stripeid: "",
      loginAttempts: 0,
    };
    const q = query(collection(db, "users"), where("createdTime", "==", ownerId));
    const querySnapshot = await getDocs(q);
    let docData: User | undefined;
    querySnapshot.forEach((doc) => {
      docData = doc.data() as User;
      seller.createdTime = docData.createdTime;
      seller.email = docData.email;
      seller.isVerified = docData.isVerified;
      seller.stripeid = docData.stripeid;
    });
    return seller;
  } catch (error) {
    console.error(error);
    throw new Error("Error fetching seller details.");
  }
};

const resetProductsViews = async () => {
  try {
    const q = query(collection(db, "products"));
    const querySnapshot = await getDocs(q);
    querySnapshot.forEach(async (doc) => {
      const productData = doc.data() as Product;
      productData.documentId = doc.id;
      await updateProduct(productData, { views: 0 });
    });
  } catch (error) {
    console.error(error);
  }
};

export const deleteProduct =
  (productData: Product) => async (dispatch: Dispatch) => {
    try {
      // Update the product to mark it as deleted by setting productOwner to empty and productQuantity to 0
      await updateProduct(productData, {
        productOwner: "",
        productQuantity: 0,
      });

      // Return success to indicate the operation completed
      return { success: true };
    } catch (error) {
      console.error("Error deleting product: ", error);
      throw error;
    }
  };
